import { lazy, Suspense, useEffect, useState } from "react";
import Layout from "./components/Layout";
import { SmoothScroll } from "./components/SmoothScroll";
import { ScrollOverlay } from "./components/ScrollOverlay";
import { ZoneIndicator } from "./components/ZoneIndicator";
import Tokenomics from "./components/Tokenomics";
import Presale from "./components/Presale";
import Staking from "./components/Staking";
import Roadmap from "./components/Roadmap";
import Trust from "./components/Trust";
import Footer from "./components/Footer";

/**
 * Code-split the entire spatial bundle. The bare 2D experience must paint
 * within ~600KB gzipped of initial JS; three / @react-three / @react-three/xr
 * together are ~800KB and we'd rather pay that lazily after first paint
 * than block first useful frame.
 */
const SpatialExperience = lazy(() =>
  import("./components/spatial/SpatialMount").then((m) => ({ default: m.SpatialMount })),
);

export default function App() {
  // Detect if WebGL is even available — plug in a static fallback otherwise.
  const [glOk, setGlOk] = useState<boolean | null>(null);
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      const gl = c.getContext("webgl2") || c.getContext("webgl");
      setGlOk(!!gl);
    } catch {
      setGlOk(false);
    }
  }, []);

  // Spatial scene gated to opt-in via ?spatial=1 query (or future toggle).
  // The full XR/3D corridor is heavy (1.4MB three.js + 218KB r3f) and was
  // crashing on some hardware — defaulting to the 2D experience until the
  // perf budget on lower-end devices is confirmed. Wallet + all six widgets
  // still render normally below.
  const wantSpatial =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("spatial") === "1";

  return (
    <>
      <SmoothScroll />
      <Layout>
        {wantSpatial && glOk !== false && (
          <>
            <Suspense fallback={null}>
              <SpatialExperience />
            </Suspense>
            <ScrollOverlay />
            <div className="relative" style={{ height: "600vh" }} aria-hidden />
            <ZoneIndicator />
          </>
        )}

        <div className="relative z-10 bg-gradient-to-b from-transparent via-background to-background">
          <Presale />
          <Tokenomics />
          <Staking />
          <Roadmap />
          <Trust />
          <Footer />
        </div>
      </Layout>
    </>
  );
}
